import React from "react";

import { faPlus, faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { twJoin } from "tailwind-merge";

import { Button } from "src/base-components/Button";
import {
  buildFilterCondition,
  DEFAULT_JUNCTION,
  MAX_CONDITIONS_PER_GROUP,
} from "src/base-components/FilterPopover/constants";
import { FilterCondition } from "src/base-components/FilterPopover/FilterCondition";
import { JunctionDropdown } from "src/base-components/FilterPopover/JunctionDropdown";
import {
  FilterConditionMode,
  FilterConfig,
  FilterFormData,
  Junction,
} from "src/base-components/FilterPopover/types";

type FilterGroupProps = FilterConfig & {
  groupIndex: number;
  mode: FilterConditionMode;
  onRemove: (() => void) | undefined;
  /** When true, renders conditions without the group wrapper (no junction column, no gray box) */
  flat?: boolean;
};

export const FilterGroup: React.FC<FilterGroupProps> = ({
  fields,
  sections,
  valueRenderers,
  operatorOptions,
  fieldOverrides,
  groupIndex,
  mode,
  onRemove,
  maxConditionsPerGroup = MAX_CONDITIONS_PER_GROUP,
  flat = false,
  allowedJunctions,
}) => {
  const form = useFormContext<FilterFormData>();
  const {
    fields: conditions,
    append,
    remove,
  } = useFieldArray<FilterFormData>({
    name: `filter_condition.conditions.${groupIndex}.conditions`,
  });

  const handleConditionJunctionChange = (selectedJunction: Junction) => {
    conditions.forEach((_, idx) => {
      if (idx > 1) {
        form.setValue(
          `filter_condition.conditions.${groupIndex}.conditions.${idx}.junction`,
          selectedJunction,
        );
      }
    });
  };

  const handleAddCondition = () => {
    const currentJunction: Junction =
      conditions.length > 1
        ? (form.getValues(
            `filter_condition.conditions.${groupIndex}.conditions.1.junction`,
          ) as Junction) || DEFAULT_JUNCTION
        : DEFAULT_JUNCTION;
    append(buildFilterCondition(currentJunction));
  };

  const handleRemoveCondition = (index: number) => {
    if (conditions.length > 1) {
      remove(index);
    }
  };

  const conditionsList = (
    <>
      {conditions.map((condition, index) => (
        <FilterCondition
          key={condition.id}
          allowedJunctions={allowedJunctions}
          canRemove={conditions.length > 1}
          conditionIndex={index}
          fieldOverrides={fieldOverrides}
          fields={fields}
          groupIndex={groupIndex}
          mode={index === 0 ? "where" : "junction"}
          operatorOptions={operatorOptions}
          sections={sections}
          valueRenderers={valueRenderers}
          onJunctionChange={
            index === 1 ? handleConditionJunctionChange : undefined
          }
          onRemove={() => handleRemoveCondition(index)}
        />
      ))}
      <div>
        <Button
          dataLoc={`add-condition-${groupIndex}`}
          disabled={conditions.length >= maxConditionsPerGroup}
          iconLeft={faPlus}
          size="sm"
          variant="secondary"
          onClick={handleAddCondition}
        >
          Add filter
        </Button>
      </div>
    </>
  );

  if (flat) {
    return <div className="flex flex-col gap-y-3">{conditionsList}</div>;
  }

  return (
    <div className="flex gap-x-4">
      <div
        className={twJoin(
          "py-1.5",
          allowedJunctions?.length === 1 ? "basis-12" : "basis-[72px]",
        )}
      >
        {mode === "where" ? (
          <span className="font-inter-medium-12px text-gray-700">Where</span>
        ) : allowedJunctions?.length === 1 ? (
          <span className="font-inter-medium-12px text-gray-700">
            {allowedJunctions[0]}
          </span>
        ) : (
          <Controller
            name={`filter_condition.conditions.${groupIndex}.junction`}
            render={({ field }) => (
              <JunctionDropdown
                dataLoc={`group-${groupIndex}-junction`}
                disabled={groupIndex > 1}
                selectedKey={field.value}
                onSelect={(key) => {
                  field.onChange(key);
                  if (groupIndex === 1) {
                    const groups = form.getValues(
                      "filter_condition.conditions",
                    );
                    groups.forEach((_, idx) => {
                      if (idx > 1) {
                        form.setValue(
                          `filter_condition.conditions.${idx}.junction`,
                          key as Junction,
                        );
                      }
                    });
                  }
                }}
              />
            )}
          />
        )}
      </div>
      <div className="flex flex-grow flex-col gap-y-3 rounded-lg bg-gray-50 p-3">
        {onRemove && (
          <div className="flex justify-end">
            <Button
              dataLoc={`delete-group-${groupIndex}`}
              iconLeft={faTrashCan}
              size="sm"
              variant="secondary"
              onClick={onRemove}
            >
              Delete filter group
            </Button>
          </div>
        )}
        {conditionsList}
      </div>
    </div>
  );
};
